import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Map, MapPin, Plus, Pen, Trash2, Search, X as IconX, GitBranch, Warehouse, Power } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  zonesOf, addZone, updateZone, deleteZone,
  branchesOfZone, warehousesOfZone,
  masterStates, masterDistricts, isModuleOn,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { SideDrawer } from '../../components/SideDrawer';
import { CustomSelect } from '../../components/CustomSelect';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';

const formatName = (val) => val.replace(/[^a-zA-Z\s]/g, '').replace(/\b\w/g, c => c.toUpperCase());

const blank = (orgId) => ({ id: null, orgId, name: '', description: '', zoneState: '', zoneDistrict: '', zoneStatus: 'Active' });

const Field = ({ label, icon: Icon, error, required, children }) => (
  <label className="block space-y-1">
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
      {label} {required && <span className="text-rose-500 ml-1">*</span>}
    </span>
    <span className="relative block">
      {Icon && (
        <span className={`absolute left-3 top-1/2 -translate-y-1/2 z-30 ${error ? 'text-rose-400' : 'text-slate-400'}`} aria-hidden="true">
          <Icon size={14} />
        </span>
      )}
      {React.cloneElement(children, {
        className: `w-full rounded-lg bg-gray-50 border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? 'pl-9' : ''}`,
      })}
    </span>
    {error && <p className="text-[11px] font-medium text-rose-500 mt-1">{error}</p>}
  </label>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  error: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.element.isRequired,
};

const ZoneModal = ({ open, draft, setDraft, onClose, onSubmit, mode, accent, showNotification }) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  if (!open) return null;

  const activeStates = masterStates.filter(s => s.status === 'Active');
  const selectedState = activeStates.find(s => s.name === draft.zoneState);
  const availableDistricts = selectedState ? masterDistricts.filter(d => d.stateId === selectedState.id && d.status === 'Active') : [];

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Please fill the Zone name.';
    else if (draft.name.trim().length < 3) newErrors.name = 'Zone name must be at least 3 characters.';

    if (!draft.zoneState) newErrors.zoneState = 'Please select a State.';
    if (!draft.zoneDistrict) newErrors.zoneDistrict = 'Please select a District.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };

  return (
    <SideDrawer onClose={onClose}>
      <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}><Map size={18} /></div>
          <div>
            <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit zone' : 'New zone'}</h3>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Group branches & warehouses</p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
      </header>
      <div className="px-6 py-5 flex flex-col gap-4 flex-1 overflow-y-auto custom-scrollbar">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Zone name" icon={MapPin} error={errors.name} required>
            <input type="text" value={draft.name} onChange={(e) => {
              setDraft({ ...draft, name: formatName(e.target.value) });
              if (errors.name) setErrors({ ...errors, name: undefined });
            }} placeholder="e.g. Pondicherry" />
          </Field>

          <Field label="Zone Status" error={errors.zoneStatus}>
            <CustomSelect
              value={draft.zoneStatus || 'Active'}
              onChange={(val) => setDraft({ ...draft, zoneStatus: val })}
              options={[
                { label: 'ACTIVE', value: 'Active', icon: <Power size={14} className="text-emerald-500" /> },
                { label: 'INACTIVE', value: 'Inactive', icon: <Power size={14} className="text-rose-500" /> }
              ]}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Zone State" icon={Map} error={errors.zoneState} required>
            <CustomSelect
              value={draft.zoneState || ''}
              onChange={(val) => {
                setDraft({ ...draft, zoneState: val, zoneDistrict: '' });
                if (errors.zoneState || errors.zoneDistrict) setErrors({ ...errors, zoneState: undefined, zoneDistrict: undefined });
              }}
              placeholder="Select a State"
              options={activeStates.map(s => ({ label: s.name, value: s.name }))}
              className="normal-case font-medium"
            />
          </Field>

          <Field label="Zone District" icon={MapPin} error={errors.zoneDistrict} required>
            <CustomSelect
              value={draft.zoneDistrict || ''}
              onChange={(val) => {
                setDraft({ ...draft, zoneDistrict: val });
                if (errors.zoneDistrict) setErrors({ ...errors, zoneDistrict: undefined });
              }}
              disabled={!draft.zoneState}
              placeholder={draft.zoneState ? 'Select a District' : 'Select a State first'}
              options={availableDistricts.map(d => ({ label: d.name, value: d.name }))}
              className="normal-case font-medium"
            />
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="What this zone covers"
            rows="2"
            className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:border-[var(--theme-primary)] focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors resize-none"
          />
        </Field>
      </div>
      <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
        <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
          style={{ background: accent }}
        >
          {mode === 'edit' ? 'Save zone' : 'Create zone'}
        </button>
      </footer>
    </SideDrawer>
  );
};

ZoneModal.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
};

export const ZonesTab = ({ org, showNotification }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);

  const warehouseEnabled = isModuleOn(org, 'warehouse');

  const list = useMemo(() => {
    const all = zonesOf(org.id);
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((z) =>
      (z.name || '').toLowerCase().includes(q) || (z.description || '').toLowerCase().includes(q));
  }, [search, org.id, version, dataVersion]);

  const accent = org.theme.primary;

  const openCreate = () => { setDraft(blank(org.id)); setModalMode('create'); setModalOpen(true); };
  const openEdit = (z) => { setDraft({ ...z }); setModalMode('edit'); setModalOpen(true); };

  const submit = (record) => {
    if (modalMode === 'create') {
      addZone({
        orgId: org.id,
        name: record.name.trim(),
        description: (record.description || '').trim(),
        zoneState: (record.zoneState || '').trim(),
        zoneDistrict: (record.zoneDistrict || '').trim(),
        zoneStatus: record.zoneStatus || 'Active'
      });
      if (showNotification) showNotification(`Zone "${record.name}" created.`, 'success');
    } else {
      updateZone(record.id, {
        name: record.name.trim(),
        description: (record.description || '').trim(),
        zoneState: (record.zoneState || '').trim(),
        zoneDistrict: (record.zoneDistrict || '').trim(),
        zoneStatus: record.zoneStatus || 'Active'
      });
      if (showNotification) showNotification('Zone updated.', 'success');
    }
    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    const ok = deleteZone(confirmDelete.id);
    if (showNotification) {
      showNotification(
        ok ? `Zone "${confirmDelete.name}" removed.` : 'Move its branches and warehouses out first.',
        ok ? 'success' : 'error',
      );
    }
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  const toggleStatus = (z) => {
    const newStatus = z.zoneStatus === 'Active' ? 'Inactive' : 'Active';
    updateZone(z.id, {
      name: z.name,
      description: z.description,
      zoneState: z.zoneState,
      zoneDistrict: z.zoneDistrict,
      zoneStatus: newStatus
    });
    if (showNotification) showNotification(`Zone marked as ${newStatus}`, 'success');
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4 h-fit">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search zones…"
              aria-label="Search zones"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors shadow-lg"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
        >
          <Plus size={14} aria-hidden="true" /> New zone
        </button>
      </section>
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg h-fit">
        <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[var(--theme-tertiary)] bg-gray-50/50">
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%]">Zone Name</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">State</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">District</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[12%]">Branches</th>
                {warehouseEnabled && <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[12%]">Warehouses</th>}
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[12%]">Status</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[14%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((z) => {
                const branches = branchesOfZone(z.id);
                const whs = warehousesOfZone(z.id);
                return (
                  <tr key={z.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-[13px] font-extrabold text-[#0d0d0d] uppercase tracking-wide">{z.name}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[12px] font-semibold text-slate-700">{z.zoneState || '—'}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[12px] font-medium text-slate-500">{z.zoneDistrict || '—'}</p>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-slate-700">
                        {branches.length}
                      </span>
                    </td>
                    {warehouseEnabled && (
                      <td className="px-5 py-3 text-center">
                        <span className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-slate-700">
                          {whs.length}
                        </span>
                      </td>
                    )}
                    <td className="px-5 py-3 text-center">
                      <StatusToggle
                        on={(z.zoneStatus || 'Active') === 'Active'}
                        label={z.name}
                        onChange={() => toggleStatus(z)}
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <RowActions>
                        <IconAction icon={Pen} label={`Edit ${z.name}`} title="Edit" onClick={() => openEdit(z)} />
                        <IconAction
                          icon={Trash2}
                          tone="danger"
                          label={`Remove ${z.name}`}
                          title={(branches.length > 0 || whs.length > 0) ? 'Move branches and warehouses out before removing' : 'Remove'}
                          disabled={branches.length > 0 || whs.length > 0}
                          onClick={() => setConfirmDelete(z)}
                        />
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-5 py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                    No zones yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      <ZoneModal
        open={modalOpen}
        draft={draft}
        setDraft={setDraft}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        mode={modalMode}
        accent={accent}
        showNotification={showNotification}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">The zone will be removed from this organization.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ZonesTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
